import { calculatePlayerRating } from "@/utils/playerRating";

export type GuestBalanceHint = "unknown" | "regular" | "strong";

export interface GuestProfile {
  appearances: number;
  balanceScore: number;
  lastSeen?: string;
  hint?: GuestBalanceHint;
}

export type GuestProfileCache = Record<string, GuestProfile>;

const HINT_OFFSET: Record<GuestBalanceHint, number> = {
  unknown: -5,
  regular: 0,
  strong: 10,
};

const MIN_SCORE = 1;
const MAX_SCORE = 100;
const PROMOTE_APPEARANCES = 3;

export function normalizeGuestName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function clampBalanceScore(value: number): number {
  return Math.max(MIN_SCORE, Math.min(MAX_SCORE, Math.round(value)));
}

/** Default balance for a guest given group average, optional hint, and name cache. */
export function computeGuestBalanceScore(
  groupAverage: number,
  options: {
    hint?: GuestBalanceHint | null;
    cached?: GuestProfile | null;
  } = {}
): number {
  if (options.cached?.balanceScore != null) {
    return clampBalanceScore(options.cached.balanceScore);
  }

  const hint = options.hint ?? "unknown";
  return clampBalanceScore(groupAverage + HINT_OFFSET[hint]);
}

export function groupAverageRating(
  players: { skills?: number; effort?: number; stamina?: number }[]
): number {
  if (players.length === 0) return 75;
  const sum = players.reduce((acc, p) => acc + calculatePlayerRating(p), 0);
  return Math.round(sum / players.length);
}

export function playerBalanceScore(player: {
  skills?: number;
  effort?: number;
  stamina?: number;
}): number {
  return calculatePlayerRating(player);
}

export function shouldSuggestGuestPromotion(
  guestName: string,
  cache: GuestProfileCache,
  rsvpCount = 0
): boolean {
  const key = normalizeGuestName(guestName);
  const profile = cache[key];
  const appearances = Math.max(profile?.appearances ?? 0, rsvpCount);
  return appearances >= PROMOTE_APPEARANCES;
}

/** Nudge cached guest score after a match (conservative deltas). */
export function adjustGuestProfileAfterMatch(
  profile: GuestProfile,
  won: boolean
): GuestProfile {
  const delta = won ? 1 : -1;
  return {
    ...profile,
    appearances: profile.appearances + 1,
    balanceScore: clampBalanceScore(profile.balanceScore + delta),
    lastSeen: new Date().toISOString().slice(0, 10),
  };
}

export function upsertGuestProfile(
  cache: GuestProfileCache,
  guestName: string,
  profile: GuestProfile
): GuestProfileCache {
  const key = normalizeGuestName(guestName);
  if (!key) return cache;
  return { ...cache, [key]: profile };
}

export function getGuestProfile(
  cache: GuestProfileCache,
  guestName: string
): GuestProfile | null {
  const key = normalizeGuestName(guestName);
  return cache[key] ?? null;
}
